import format from 'date-fns/format';
import { ja } from 'date-fns/locale';
import { DateTime } from 'luxon';
import { useTranslation } from 'next-i18next';

import { useCountdown } from '../../../hooks/useCountdown';
import { isInNext24Hours, isInNextSixDays } from '../../../utils/product';

type Props = {
  date: Date;
  addDaysToHours?: boolean;
  uniqueDateFormat?: string;
  keyCountdownDate?: string;
  keyCountdownDays?: string;
  keyCountdownTime?: string;
  sixDaysGateway?: boolean;
};

const Countdown = ({
  addDaysToHours = false,
  date,
  keyCountdownDays = 'countdown.days',
  keyCountdownTime = 'countdown.time',
}: Props) => {
  const { t } = useTranslation('common');
  const { destructuredDate } = useCountdown({
    addDaysToHours,
    date,
  });

  return isInNext24Hours(date)
    ? t(keyCountdownTime, destructuredDate)
    : t(keyCountdownDays, destructuredDate);
};

const ProductDate = ({
  addDaysToHours = false,
  date,
  keyCountdownDate,
  keyCountdownDays = 'countdown.days',
  keyCountdownTime = 'countdown.time',
  sixDaysGateway = true,
  uniqueDateFormat,
}: Props) => {
  const { i18n, t } = useTranslation('common');
  const isJapanese = i18n.language === 'ja';
  const dateFnsLocale = isJapanese ? ja : undefined;

  // English:  "Monday, 22 June – 10:00 GMT+1"
  // Japanese: "6月22日 (月) 10:00 (英国夏時間)"
  // Same instant and viewer timezone in both — only the format and the
  // timezone label are localised (the label comes from the viewer's zone).
  const timeZone = isJapanese
    ? `(${DateTime.local().setLocale('ja').offsetNameLong})`
    : DateTime.local().offsetNameShort;

  const dateFormat = isJapanese ? 'M月d日 (E) HH:mm' : 'cccc, dd MMMM – HH:mm';

  const dateStr = uniqueDateFormat
    ? format(date, uniqueDateFormat, { locale: dateFnsLocale })
    : `${format(date, dateFormat, { locale: dateFnsLocale })} ${timeZone}`;

  const showCountDown = sixDaysGateway
    ? isInNextSixDays(date)
    : isInNext24Hours(date);

  let content;

  if (showCountDown) {
    content = (
      <Countdown
        addDaysToHours={addDaysToHours}
        date={date}
        keyCountdownDays={keyCountdownDays}
        keyCountdownTime={keyCountdownTime}
      />
    );
  } else if (keyCountdownDate) {
    content = t(keyCountdownDate, { date: dateStr });
  } else {
    content = dateStr;
  }

  return (
    <time dateTime={date.toISOString()} title={dateStr}>
      {content}
    </time>
  );
};

export default ProductDate;
